/**
 * The upload policy, in one place for every upload path (AppSec T-011, M-025).
 *
 * Uploads go from the browser straight to S3 with a presigned POST — not
 * through CloudFront, so the WAF never sees the bytes. That is deliberate (a
 * CDN in the upload path fights the presigned signature for little gain), and it
 * makes the presigned POLICY the control: S3 itself refuses anything outside it,
 * at upload time, before a byte is stored.
 *
 * Previously these were presigned PUTs, and a PUT cannot carry a size limit: a
 * single URL accepted up to 5 GB for 15 minutes.
 *
 * Pure — no AWS SDK — so the clients, both APIs and the jobs can all import it.
 * The two API adapters turn it into a presigned POST; the variants job uses the
 * same limit as a backstop for anything that reached the bucket another way.
 */

/**
 * 15 MB. Comfortably above a full-resolution JPEG from a current phone camera
 * (the game uploads photos as taken, without downscaling), and five hundred
 * times below what a presigned PUT allowed.
 */
export const MAX_UPLOAD_BYTES = 15 * 1024 * 1024;

/**
 * Five minutes — the upload starts the moment the URL is issued, so it needs
 * seconds, not the quarter-hour PUT URLs had. It also bounds what the kill switch
 * cannot stop: an upload URL issued just before closing.
 */
export const UPLOAD_URL_TTL_SECONDS = 300;

/** The two condition shapes the policy uses — structurally `createPresignedPost`'s own. */
export type UploadCondition = ["content-length-range", number, number] | ["eq", string, string];

/** What S3 must enforce on a presigned POST, for `createPresignedPost`. */
export function uploadPolicy(contentType: string): {
  conditions: UploadCondition[];
  fields: Record<string, string>;
  expiresSeconds: number;
} {
  return {
    conditions: [
      // At least one byte: an empty object would later fail every decoder.
      ["content-length-range", 1, MAX_UPLOAD_BYTES],
      // The type the server allowlisted when it issued the URL — signed, so the
      // client cannot swap in something else after the fact.
      ["eq", "$Content-Type", contentType],
    ],
    fields: { "Content-Type": contentType },
    expiresSeconds: UPLOAD_URL_TTL_SECONDS,
  };
}

/**
 * The multipart body for a presigned POST. S3 reads the form in order and
 * ignores everything after the file, so the signed fields go first and the
 * file last. The browser sets the multipart Content-Type header itself — do not.
 */
export function uploadForm(fields: Record<string, string>, file: Blob): FormData {
  const form = new FormData();
  for (const [name, value] of Object.entries(fields)) form.append(name, value);
  form.append("file", file);
  return form;
}
